/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */
export class OptionTree {
    constructor(object, optionWeights) {
        this.tree = object || {};
        this.optionWeights = optionWeights || {};
        this.uniqueOptions = {};
        this.pathList = [];
    }

    /**
     * @descriptions Takes a selection with any order, and spits out a consistent structure and order.
     * @param {[string]} path
     * @returns
     */
    cleanPath(path) {
        const filteredPath = path.filter((values) => {
            if (values.length <= 0) {
                return false;
            }

            return true;
        });

        const flatPath = filteredPath.flat();

        const sortedPath = flatPath.sort((a, b) => {
            const aWeight = this.optionWeights[a];
            const bWeight = this.optionWeights[b];

            // in the event where 2 paths are the same, switch to string comparison
            if (aWeight === bWeight) {
                return a < b ? -1 : 1;
            }

            return aWeight < bWeight ? -1 : 1;
        });

        return sortedPath;
    }

    /**
     * @internal helper for this.buildOptionTree
     *
     * @description Places the designated sku at the end of the passed in path.
     * @param {object} sku Whatever the sku should be set to.
     * @param {[string]} path result of getPathFromSku helper function/
     * @returns undefined
     */
    setSkuForPath(sku, path) {
        // at this point, sku has been parsed and we need to build the option tree using the path
        let pointer = this.tree;
        path.forEach((value, index) => {
            // intialize new paths
            if (!pointer[value]) {
                pointer[value] = {};
            }

            // sidenote: record unique values to quickly know what options are avaialble
            this.uniqueOptions[value] = true;

            // if its the last along the path, place the sku info for reference
            if (index === path.length - 1) {
                pointer[value].sku = sku;
            }

            pointer = pointer[value];
        });
    }

    /**
     * @description Takes in raw data to produce an OptionTree
     * @param {array} productSkus
     * @returns OptionTree
     */
    buildOptionTree(skuTuple) {
        skuTuple.forEach(({ options, sku }) => {
            const path = this.cleanPath(options);
            this.pathList.push(path);
            this.setSkuForPath(sku, path);
        });
    }

    /**
     * @internal used by getOptionsFromSelection and getSkuFromSelection
     *
     * @description Best effort attempt to traverses a tree from a given path and returns the node
     * @param {[string]]]} path
     * @returns an array of remaining options
     */
    findEndOfPath(path) {
        // since the path is now ordered, should be able to just walk the tree
        // in a similar fashion to building it

        // reverse the array to enable efficient pop function
        const leftover = path.slice().reverse();
        let remainingOptions = this.tree;

        while (leftover.length) {
            // pop off the array
            const selection = leftover.pop();

            // update the pointer
            if (remainingOptions) {
                remainingOptions = remainingOptions[selection];
            }
        }

        // return the node
        return remainingOptions;
    }

    /**
     * @external
     *
     * @description Recieves a selection and returns the remaining possible options.
     * @param {[[string]]} rawPath
     * @returns {[string]}
     */
    getOptionsFromSelection(rawPath) {
        const recurse = (goodPath) => {
            // grab the furthest down point on the tree given the selection
            const selectionResult = this.findEndOfPath(goodPath);

            // if there are no more options, provide empty array
            if (!selectionResult) {
                return [];
            }

            // turn the selection result in an array and remove sku property
            const children = Object.keys(selectionResult).filter((value) => value !== 'sku');

            let options = [...children];
            // for each child we need to step 1 option further to get children of children
            children.forEach((child) => {
                // repeat with the immediate child passed it to get children of children
                options = options.concat(recurse([...goodPath, child]));
            });

            // return the options found to the previous call
            return options;
        };

        const cleanPath = this.cleanPath(rawPath);
        const options = recurse(cleanPath);

        return options;
    }

    /**
     * @external
     *
     * @description Takes a selection and returns a sku if the selection is valid.
     * @param {[[string]]} rawPath
     * @returns {{}}
     */
    getSkuFromSelection(rawPath) {
        const cleanPath = this.cleanPath(rawPath);
        const endOfPath = this.findEndOfPath(cleanPath);

        // endOfPath might be undefined if the path is not a valid selection
        if (!endOfPath || !endOfPath.sku) {
            return null;
        }

        return endOfPath.sku;
    }

    /**
     * @external
     *
     * @description Takes a raw incomplete path and provides a list of all paths
     * containing all selections.
     * @param {[string]} rawPath
     * @returns {[[string]]}
     */
    getPathsThatContainSelection(rawPath) {
        const cleanPath = this.cleanPath(rawPath);

        // 1. make a copy of the path list
        let pathList = JSON.parse(JSON.stringify(this.pathList));

        // 2. filter out paths that dont contain a single one of the options, start at the end first
        // as they are more obscure reducing the list faster
        const reversePath = cleanPath.slice().reverse();
        reversePath.forEach((option) => {
            pathList = pathList.filter((path) => path.includes(option));
        });

        // 3. find paths that contain all options (should be pretty narrow now)
        const output = pathList.filter((path) => {
            for (let i = 0; i < cleanPath.length; i += 1) {
                const option = cleanPath[i];
                if (!path.includes(option)) {
                    return false;
                }
            }

            return true;
        });

        return output;
    }
}

export default { OptionTree };
